import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import GuestLayout from '../../components/GuestLayout';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';

const RESERVATIONS_URL = `${API_BASE_URL}/reservations`;

const OCCASIONS = [
    '',
    'Birthday Celebration',
    'Marriage Anniversary',
    'Family / Friends Party',
    'Business Dinner',
    'Romantic Dinner',
    'Other',
];

const DineInReservation = () => {
    const { restaurantId } = useParams();
    const navigate = useNavigate();
    const [restaurant, setRestaurant] = useState(null);
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const [form, setForm] = useState({
        customerName: '',
        email: '',
        contactNumber: '',
        numberOfPeople: 1,
        reservationDate: '',
        reservationTime: '',
        occasion: '',
        message: '',
        specialInstructions: '',
        selectedItems: [],
    });

    useEffect(() => {
        if (!restaurantId) return;
        const fetchData = async () => {
            try {
                setLoading(true);
                const [restRes, menuRes] = await Promise.all([
                    axios.get(API_ENDPOINTS.RESTAURANT_BY_ID(restaurantId)),
                    axios.get(`${API_ENDPOINTS.MENU}?restaurantId=${restaurantId}`),
                ]);
                setRestaurant(restRes.data);
                setMenuItems(Array.isArray(menuRes.data) ? menuRes.data.filter(m => m.isVisible !== false) : []);
            } catch (e) {
                setError('Could not load restaurant or menu.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [restaurantId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'contactNumber') {
            const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
            setForm((prev) => ({ ...prev, [name]: digitsOnly }));
            return;
        }
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const addMenuItem = (item) => {
        setForm((prev) => {
            const existing = prev.selectedItems.find((i) => i.menuItemId === item._id);
            if (existing) {
                return {
                    ...prev,
                    selectedItems: prev.selectedItems.map((i) =>
                        i.menuItemId === item._id ? { ...i, quantity: i.quantity + 1 } : i
                    ),
                };
            }
            return {
                ...prev,
                selectedItems: [
                    ...prev.selectedItems,
                    { menuItemId: item._id, name: item.itemName, price: item.itemPrice, quantity: 1 },
                ],
            };
        });
    };

    const removeMenuItem = (menuItemId) => {
        setForm((prev) => ({
            ...prev,
            selectedItems: prev.selectedItems.filter((i) => i.menuItemId !== menuItemId),
        }));
    };

    const updateItemQuantity = (menuItemId, delta) => {
        setForm((prev) => ({
            ...prev,
            selectedItems: prev.selectedItems
                .map((i) =>
                    i.menuItemId === menuItemId
                        ? { ...i, quantity: Math.max(1, i.quantity + delta) }
                        : i
                )
                .filter((i) => i.quantity > 0),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!form.customerName?.trim() || !form.email?.trim() || !form.contactNumber?.trim()) {
            setError('Please fill in name, email and contact number.');
            return;
        }
        if (!/^\d{10}$/.test(form.contactNumber.trim())) {
            setError('Contact number must be exactly 10 digits.');
            return;
        }
        if (!form.reservationDate || !form.reservationTime) {
            setError('Please select date and time.');
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(RESERVATIONS_URL, {
                restaurantId,
                customerName: form.customerName.trim(),
                email: form.email.trim(),
                contactNumber: form.contactNumber.trim(),
                numberOfPeople: Math.max(1, parseInt(form.numberOfPeople, 10) || 1),
                selectedItems: form.selectedItems,
                reservationDate: form.reservationDate,
                reservationTime: form.reservationTime,
                occasion: form.occasion.trim(),
                message: form.message.trim(),
                specialInstructions: form.specialInstructions.trim(),
            });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to submit reservation. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <GuestLayout>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" />
                </div>
            </GuestLayout>
        );
    }

    if (success) {
        return (
            <GuestLayout>
                <div className="min-h-screen flex items-center justify-center p-4">
                    <motion.div
                        className="bg-white rounded-2xl shadow-xl border border-stone-200 p-6 sm:p-8 max-w-md w-full text-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <div className="text-5xl mb-4">✅</div>
                        <h1 className="text-xl font-bold text-stone-800 mb-2">Reservation Request Sent</h1>
                        <p className="text-stone-600 text-sm mb-6">
                            We have received your request. The restaurant will confirm your table and contact you shortly.
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-4 rounded-xl transition-colors"
                        >
                            Back to Menu
                        </button>
                    </motion.div>
                </div>
            </GuestLayout>
        );
    }

    return (
        <GuestLayout>
            <div className="min-h-screen pb-12">
                <div className="bg-white border-b border-stone-200 sticky top-0 z-10 shadow-sm">
                    <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {restaurant?.logo && (
                                <img
                                    src={restaurant.logo}
                                    alt=""
                                    className="h-10 w-10 rounded-xl object-cover"
                                />
                            )}
                            <div>
                                <h1 className="text-lg font-bold text-stone-800">
                                    {restaurant?.name || 'Restaurant'}
                                </h1>
                                <p className="text-[10px] uppercase tracking-wider text-stone-500">
                                    Reserve a table
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="text-stone-500 hover:text-stone-700 text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>

                <motion.form
                    onSubmit={handleSubmit}
                    className="max-w-2xl mx-auto px-4 py-6 space-y-5"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                >
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                            {error}
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                                Name *
                            </label>
                            <input
                                type="text"
                                name="customerName"
                                value={form.customerName}
                                onChange={handleChange}
                                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-stone-800 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                                placeholder="Your name"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                                Email *
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-stone-800 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                                placeholder="your@email.com"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                            Contact Number *
                        </label>
                        <input
                            type="tel"
                            name="contactNumber"
                            value={form.contactNumber}
                            onChange={handleChange}
                            className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-stone-800 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                            placeholder="10-digit mobile number"
                            maxLength={10}
                            inputMode="numeric"
                            pattern="[0-9]{10}"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                            Number of guests *
                        </label>
                        <input
                            type="number"
                            name="numberOfPeople"
                            min={1}
                            max={100}
                            value={form.numberOfPeople}
                            onChange={handleChange}
                            className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-stone-800 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                                Date *
                            </label>
                            <input
                                type="date"
                                name="reservationDate"
                                value={form.reservationDate}
                                onChange={handleChange}
                                min={new Date().toISOString().split('T')[0]}
                                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-stone-800 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                                Time *
                            </label>
                            <input
                                type="time"
                                name="reservationTime"
                                value={form.reservationTime}
                                onChange={handleChange}
                                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-stone-800 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                            Occasion
                        </label>
                        <select
                            name="occasion"
                            value={form.occasion}
                            onChange={handleChange}
                            className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-stone-800 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 bg-white"
                        >
                            {OCCASIONS.map((opt) => (
                                <option key={opt || 'none'} value={opt}>
                                    {opt || 'Select occasion (optional)'}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                            Message (e.g. booking for anniversary)
                        </label>
                        <textarea
                            name="message"
                            value={form.message}
                            onChange={handleChange}
                            rows={2}
                            className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-stone-800 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                            placeholder="Any note for the restaurant..."
                        />
                    </div>

                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                            Pre-order from menu (optional)
                        </label>
                        <p className="text-xs text-stone-500 mb-2">
                            Select items you’d like to have on your visit. You can add quantities in the summary below.
                        </p>
                        <div className="border border-stone-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                            <ul className="divide-y divide-stone-100">
                                {menuItems.slice(0, 50).map((item) => (
                                    <li
                                        key={item._id}
                                        className="flex items-center justify-between px-3 py-2 hover:bg-stone-50"
                                    >
                                        <span className="text-sm text-stone-800 truncate flex-1">
                                            {item.itemName} — ₹{item.itemPrice}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => addMenuItem(item)}
                                            className="ml-2 text-orange-600 hover:text-orange-700 text-xs font-medium"
                                        >
                                            Add
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        {form.selectedItems.length > 0 && (
                            <div className="mt-3 p-3 bg-stone-50 rounded-xl border border-stone-200">
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-2">
                                    Selected items
                                </p>
                                <ul className="space-y-1">
                                    {form.selectedItems.map((i) => (
                                        <li
                                            key={i.menuItemId}
                                            className="flex items-center justify-between text-sm"
                                        >
                                            <span className="text-stone-700">
                                                {i.name} × {i.quantity}
                                            </span>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => updateItemQuantity(i.menuItemId, -1)}
                                                    className="w-6 h-6 rounded border border-stone-300 text-stone-600"
                                                >
                                                    −
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => updateItemQuantity(i.menuItemId, 1)}
                                                    className="w-6 h-6 rounded border border-stone-300 text-stone-600"
                                                >
                                                    +
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => removeMenuItem(i.menuItemId)}
                                                    className="text-red-600 text-xs ml-1"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">
                            Special instructions
                        </label>
                        <textarea
                            name="specialInstructions"
                            value={form.specialInstructions}
                            onChange={handleChange}
                            rows={2}
                            className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-stone-800 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                            placeholder="Dietary requirements, high chair, cake, etc."
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-stone-400 text-white font-semibold py-3.5 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                    >
                        {submitting ? (
                            <>
                                <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                                Submitting...
                            </>
                        ) : (
                            <>Send reservation request</>
                        )}
                    </button>
                </motion.form>
            </div>
        </GuestLayout>
    );
};

export default DineInReservation;
